import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { getAdminOrderDetail, updateOrderStatus, startGeneration, regenerateImages, regenerateSelected, downloadImages, getGenerationStatus } from '@/api/admin';
import { ORDER_STATUS_CONFIG } from '@/types/order';
import type { OrderStatus, GeneratedImage } from '@/types/order';

const statusButtons: { key: OrderStatus; label: string }[] = [
  { key: 'pending', label: '주문대기' },
  { key: 'paid', label: '결제완료' },
  { key: 'processing', label: '제작중' },
  { key: 'completed', label: '완료' },
  { key: 'cancelled', label: '취소' },
];

const S3_HOST = 'https://spring-ai-studio.s3.ap-northeast-2.amazonaws.com';

/** S3 키 또는 URL을 접근 가능한 전체 URL로 변환 */
const toImageUrl = (urlOrKey: string | undefined): string => {
  if (!urlOrKey) return '';
  if (urlOrKey.startsWith('http')) return urlOrKey;
  return `${S3_HOST}/${urlOrKey}`;
};

export default function AdminOrderDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedCount, setGeneratedCount] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [selectedImageIdxs, setSelectedImageIdxs] = useState<Set<number>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: order, loading, error, refetch } = useFetch(
    () => getAdminOrderDetail(id!),
    [id],
  );

  console.log("[AdminOrderDetail] id:", id, "order:", order, "error:", error);

  // Sync generated images from order data
  useEffect(() => {
    if (order?.generatedImages && order.generatedImages.length > 0) {
      setGeneratedImages(order.generatedImages);
    }
  }, [order]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const startPolling = useCallback(() => {
    if (!id) return;
    pollingRef.current = setInterval(async () => {
      try {
        const job = await getGenerationStatus(id);
        setGenerationProgress(job.progress ?? 0);
        setGeneratedCount(job.generatedCount ?? 0);
        setTotalImages(job.totalImages ?? 0);
        if (job.status === 'completed') {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setIsGenerating(false);
          if (job.generatedImages) {
            setGeneratedImages(job.generatedImages);
          }
          refetch();
        } else if (job.status === 'failed') {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setIsGenerating(false);
        }
      } catch {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setIsGenerating(false);
      }
    }, 2000);
  }, [id, refetch]);

  const handleGenerateImages = useCallback(async () => {
    if (!id) return;
    setIsGenerating(true);
    setGenerationProgress(0);
    setGeneratedCount(0);

    try {
      const result = await startGeneration(id);
      setTotalImages(result.totalImages ?? 0);
      startPolling();
    } catch {
      setIsGenerating(false);
    }
  }, [id, startPolling]);

  const handleRegenerateImages = useCallback(async () => {
    if (!id) return;
    if (!confirm('기존 생성 이미지를 삭제하고 다시 생성하시겠습니까?')) return;
    setIsGenerating(true);
    setGenerationProgress(0);
    setGeneratedCount(0);
    setGeneratedImages([]);

    try {
      const result = await regenerateImages(id);
      setTotalImages(result.totalImages ?? 0);
      startPolling();
    } catch {
      setIsGenerating(false);
    }
  }, [id, startPolling]);

  const toggleImageSelection = useCallback((imageIdx: number) => {
    setSelectedImageIdxs((prev) => {
      const next = new Set(prev);
      if (next.has(imageIdx)) next.delete(imageIdx);
      else next.add(imageIdx);
      return next;
    });
  }, []);

  /** 실제 DB ID (generatedImageIdx 우선, imageIdx fallback) */
  const getImageKey = useCallback((image: GeneratedImage) => {
    return image.generatedImageIdx ?? image.imageIdx;
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedImageIdxs((prev) => {
      if (prev.size === generatedImages.length) return new Set();
      return new Set(generatedImages.map((img, i) => getImageKey(img)));
    });
  }, [generatedImages, getImageKey]);

  const handleRegenerateSelected = useCallback(async () => {
    if (!id || selectedImageIdxs.size === 0) return;
    if (!confirm(`선택한 ${selectedImageIdxs.size}장의 이미지를 재생성하시겠습니까?`)) return;
    setIsGenerating(true);
    setGenerationProgress(0);
    setGeneratedCount(0);

    // 선택된 키에 해당하는 실제 imageIdx 목록 추출
    const selectedKeys = Array.from(selectedImageIdxs);
    const imageIdxList = selectedKeys.map((key) => {
      const img = generatedImages.find((img, i) => getImageKey(img) === key);
      return img?.imageIdx ?? key;
    });

    try {
      const result = await regenerateSelected(id, imageIdxList);
      setTotalImages(result.totalImages ?? 0);
      setSelectedImageIdxs(new Set());
      startPolling();
    } catch {
      setIsGenerating(false);
    }
  }, [id, selectedImageIdxs, generatedImages, getImageKey, startPolling]);

  const handleDownloadAll = useCallback(async () => {
    if (!id) return;
    setIsDownloading(true);
    try {
      await downloadImages(id);
    } finally {
      setIsDownloading(false);
    }
  }, [id]);

  const handleDownloadSelected = useCallback(async () => {
    if (!id || selectedImageIdxs.size === 0) return;
    setIsDownloading(true);
    const selectedKeys = Array.from(selectedImageIdxs);
    const imageIdxList = selectedKeys.map((key) => {
      const img = generatedImages.find((img, i) => getImageKey(img) === key);
      return img?.imageIdx ?? key;
    });
    try {
      await downloadImages(id, imageIdxList);
    } finally {
      setIsDownloading(false);
    }
  }, [id, selectedImageIdxs, generatedImages, getImageKey]);

  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (!id) return;
    try {
      await updateOrderStatus(id, newStatus);
      refetch();
    } catch {
      // handle error silently
    }
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-500 hover:bg-emerald-600';
      case 'processing':
        return 'bg-amber-500 hover:bg-amber-600';
      case 'paid':
        return 'bg-blue-500 hover:bg-blue-600';
      case 'cancelled':
        return 'bg-red-500 hover:bg-red-600';
      case 'pending':
      default:
        return 'bg-slate-500 hover:bg-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-slate-500">불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-slate-300 mb-4"></i>
          <p className="text-lg text-slate-600">주문을 찾을 수 없습니다</p>
          {error && (
            <p className="text-sm text-red-500 mt-2">{error.message}</p>
          )}
          <button
            onClick={() => navigate('/admin/orders')}
            className="mt-4 px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap"
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const uploadedPhotos = order.photos || [];
  console.log("[AdminOrderDetail] photos:", JSON.stringify(uploadedPhotos, null, 2));
  const currentStatus = order.status;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/orders')}
              className="w-10 h-10 flex items-center justify-center hover:bg-slate-100 rounded-lg transition-colors"
            >
              <i className="ri-arrow-left-line text-xl text-slate-700"></i>
            </button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">주문 상세</h1>
              <p className="text-xs text-slate-500">{order.orderId}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600">작업 상태:</span>
            <div className="flex gap-2">
              {statusButtons.map((btn) => (
                <button
                  key={btn.key}
                  onClick={() => handleStatusChange(btn.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors whitespace-nowrap ${
                    currentStatus === btn.key ? getStatusColor(btn.key) : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Customer Info & Uploaded Photos */}
          <div className="space-y-6">
            {/* Customer Info Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-user-line text-amber-500"></i>
                고객 정보
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">고객명</span>
                  <span className="text-sm font-medium text-slate-900">{order.customerName}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">연락처</span>
                  <span className="text-sm font-medium text-slate-900">{order.customerPhone}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">이메일</span>
                  <span className="text-sm font-medium text-slate-900">{(order as any).email || order.customerEmail}</span>
                </div>
                <div className="py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600 mb-2 block">선택 스타일</span>
                  <div className="space-y-2 mt-1">
                    {order.selections?.map((s: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold shrink-0">
                          {i + 1}
                        </span>
                        <span className="font-medium text-slate-900">
                          {s.styleName || s.categoryKey || '(스타일 정보 없음)'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">선택 플랜</span>
                  <span className="text-sm font-medium text-slate-900">{order.productName}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">결제 금액</span>
                  <span className="text-lg font-bold text-slate-900">{order.amount.toLocaleString()}원</span>
                </div>
              </div>
            </div>

            {/* Uploaded Photos Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-image-line text-amber-500"></i>
                업로드된 사진
              </h2>
              {uploadedPhotos.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {uploadedPhotos.map((photo: any, index) => (
                    <div key={photo.orderPhotoIdx || index} className="relative aspect-[4/5] rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={toImageUrl(photo.url || photo.s3Key)}
                        alt={`업로드 사진 ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="ri-image-line text-4xl text-slate-300 mb-2 block"></i>
                  <p className="text-sm text-slate-500">업로드된 사진이 없습니다</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - AI Generation Area */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-magic-line text-amber-500"></i>
                AI 웨딩 이미지 생성
              </h2>

              {!isGenerating && generatedImages.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-sparkling-line text-4xl text-amber-500"></i>
                  </div>
                  <p className="text-sm text-slate-600 mb-6">
                    AI가 고객의 사진을 분석하여<br />
                    아름다운 웨딩 이미지를 생성합니다
                  </p>
                  <button
                    onClick={handleGenerateImages}
                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all whitespace-nowrap"
                  >
                    <i className="ri-magic-line mr-2"></i>
                    AI 생성 시작
                  </button>
                </div>
              )}

              {isGenerating && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <i className="ri-loader-4-line text-4xl text-amber-500 animate-spin"></i>
                  </div>
                  <p className="text-lg font-medium text-slate-900 mb-2">AI 이미지 생성 중...</p>
                  <p className="text-sm text-slate-600 mb-4">
                    고객의 사진을 분석하고 있습니다<br />
                    잠시만 기다려주세요
                  </p>
                  {totalImages > 0 && (
                    <div className="max-w-xs mx-auto">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <span>{generatedCount} / {totalImages}장</span>
                        <span>{generationProgress}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${generationProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!isGenerating && generatedImages.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <p className="text-sm text-slate-600">
                        총 <strong className="text-amber-600">{generatedImages.length}장</strong>의 이미지가 생성되었습니다
                      </p>
                      <button
                        onClick={toggleSelectAll}
                        className="text-xs text-slate-500 hover:text-slate-700 underline"
                      >
                        {selectedImageIdxs.size === generatedImages.length ? '선택 해제' : '전체 선택'}
                      </button>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 mb-4">
                    <button
                      onClick={handleRegenerateImages}
                      className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-refresh-line mr-1"></i>
                      전체 재생성
                    </button>
                    <button
                      onClick={handleRegenerateSelected}
                      disabled={selectedImageIdxs.size === 0}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className="ri-refresh-line mr-1"></i>
                      선택 재생성{selectedImageIdxs.size > 0 && ` (${selectedImageIdxs.size})`}
                    </button>
                    <button
                      onClick={handleDownloadAll}
                      disabled={isDownloading}
                      className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors whitespace-nowrap disabled:opacity-40"
                    >
                      <i className={`${isDownloading ? 'ri-loader-4-line animate-spin' : 'ri-download-line'} mr-1`}></i>
                      전체 다운로드
                    </button>
                    <button
                      onClick={handleDownloadSelected}
                      disabled={selectedImageIdxs.size === 0 || isDownloading}
                      className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-600 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <i className={`${isDownloading ? 'ri-loader-4-line animate-spin' : 'ri-download-line'} mr-1`}></i>
                      선택 다운로드{selectedImageIdxs.size > 0 && ` (${selectedImageIdxs.size})`}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {generatedImages.map((image, index) => {
                      const key = getImageKey(image);
                      const isSelected = selectedImageIdxs.has(key);
                      return (
                        <div
                          key={key}
                          className={`relative group cursor-pointer rounded-lg overflow-hidden ${isSelected ? 'ring-3 ring-amber-500' : ''}`}
                          onClick={() => toggleImageSelection(key)}
                        >
                          <div className="aspect-[3/4] bg-slate-100">
                            <img
                              src={toImageUrl((image as any).s3Key || (image as any).imageUrl || image.url)}
                              alt={`생성된 이미지 ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {/* Checkbox */}
                          <div className={`absolute top-2 left-2 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                            isSelected ? 'bg-amber-500 border-amber-500' : 'bg-white/80 border-slate-300 group-hover:border-amber-400'
                          }`}>
                            {isSelected && <i className="ri-check-line text-white text-sm"></i>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}